import type {
  OperationGraphNodeDto,
  SourceContextGraphNodeDto,
} from "~/gen/api/types"
import { NO_ACTIVITY_NAME } from "~/utils/operations"

export type BrandTraceabilityGraphNode =
  | OperationGraphNodeDto
  | SourceContextGraphNodeDto

export type RenderedChild = {
  readonly node: BrandTraceabilityGraphNode
  readonly hiddenAncestors: OperationGraphNodeDto[]
}

export type ForkOriginCue = {
  readonly item: string
  readonly reference: string
  readonly activity: string
}

type BuildForkOriginCueArgs = {
  readonly forkRoot: SourceContextGraphNodeDto
  readonly translateItemType: (type: SourceContextGraphNodeDto["type"]) => string
  readonly translateActivity: (activityName: string) => string
}

export function isSourceContextGraphNode(
  node: BrandTraceabilityGraphNode
): node is SourceContextGraphNodeDto {
  return node.nodeType === "sourceContext"
}

export function isOperationGraphNode(
  node: BrandTraceabilityGraphNode
): node is OperationGraphNodeDto {
  return node.nodeType === "operation"
}

export function findTreeParent(
  nodeId: string,
  nodesById: ReadonlyMap<string, BrandTraceabilityGraphNode>
): BrandTraceabilityGraphNode | undefined {
  for (const node of nodesById.values()) {
    if (node.childrenRef.includes(nodeId)) {
      return node
    }
  }

  return undefined
}

export function buildForkOriginCue({
  forkRoot,
  translateItemType,
  translateActivity,
}: BuildForkOriginCueArgs): ForkOriginCue {
  return {
    item: translateItemType(forkRoot.type),
    reference: forkRoot.reference,
    activity: translateActivity(forkRoot.activityName),
  }
}

export function isNodeWithoutActivity(
  node: BrandTraceabilityGraphNode
): boolean {
  return (
    isOperationGraphNode(node) &&
    node.operation === "not-entered-yet" &&
    node.activityName === NO_ACTIVITY_NAME
  )
}

export function getRenderedChildren(
  node: BrandTraceabilityGraphNode,
  nodesById: ReadonlyMap<string, BrandTraceabilityGraphNode>,
  hiddenAncestors: OperationGraphNodeDto[] = []
): RenderedChild[] {
  return node.childrenRef.flatMap((childId) => {
    const childNode = nodesById.get(childId)
    if (childNode === undefined) {
      return []
    }

    if (isOperationGraphNode(childNode) && isNodeWithoutActivity(childNode)) {
      return getRenderedChildren(childNode, nodesById, [
        ...hiddenAncestors,
        childNode,
      ])
    }

    return [{ node: childNode, hiddenAncestors }]
  })
}
